import './login.scss';
import _ from 'lodash'
import { get } from 'lodash';
import { FORM_ERROR } from 'final-form';
import { race } from 'rxjs';
import { first, map, mapTo, tap } from 'rxjs/operators';
import { defineComponent } from 'vue';

import { AuthentificationMethod } from '@skinsoft/types'
import { userSelectors, workspaceSelectors } from '@skinsoft/selectors';
import { cleanDateFormat, getI18nLocaleFromAppLocale } from '@skinsoft/utils'
import { UserAuthenticatedResult } from '@skinsoft/auth';
import { fetch } from '@skinsoft/network';
import { fetchAllLocales } from '@skinsoft/i18n/src/fetch-locale';
import { TranslationDataStore } from '@skinsoft/entity';
import { reactiveVueMixin } from '@skinsoft/reactive-vue';
import { LoginForm } from '../login-form';

declare const API_ENDPOINT: string
declare const APP_NAME: string

export const Login = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sLoginForm: LoginForm,
    },
    props: ['homeRoute'],
    template: require('./login.html'),
    data() {
        return {
            authentificationMethods: [] as AuthentificationMethod[],
            requestedTryphonLogin: false,
            loginErrorMessage: '',
        }
    },
    computed: {
        SSOAvailable(): boolean {
            return this.authentificationMethods?.length > 1
                && !this.requestedTryphonLogin
        },
    },
    methods: {
        getAuthentificationMethods() {
            this.$authService.getAuthentificationMethods().subscribe(authentificationMethods => {
                if(authentificationMethods) {
                    this.authentificationMethods = authentificationMethods
                    this.$forceUpdate()
                }
            })
        },
        onClickAuthenticationMethod(am: AuthentificationMethod) {
            if (am && am.name) {
                if (am.samlEndpoint) {
                    const appname = APP_NAME.toLowerCase().replace('_', '-')
                    const reporterEndpoint = API_ENDPOINT.replace('api.', `${appname}.`)

                    location.assign(reporterEndpoint + '/saml2/authenticate/' + am.samlEndpoint)

                } else {
                    this.requestedTryphonLogin = true
                }
            }
        },
        async onSubmit(values: Record<'username' | 'password', string>) {
            await this.$authService.logIn(values.username, values.password);

            const success$ = this.$authService.waitFor$('authenticated').pipe(mapTo(null));
            const error$ = this.$authService.waitFor$('unauthenticated').pipe(
                map(value => {
                    const [, event] = value;
                    const error = get(event, 'query.error', null);

                    if (!error) {
                        return null;
                    }

                    return {
                        [FORM_ERROR]: fetch.isNetworkError(error)
                            ? 'auth.login.error.network'
                            : 'auth.login.error.credentials',
                    };
                }),
            );

            return await race(success$, error$)
                .pipe(first())
                .toPromise();
        },
        async onUser(authResult: UserAuthenticatedResult | null) {
            if (authResult && authResult.user) {
                if(localStorage.getItem('workspace') == null){
                    const defaultWorkspace = userSelectors.defaultWorkspace(authResult.user)
                    const defaultWorkspaceId = defaultWorkspace?.workspaceId ?? defaultWorkspace?.id
                    if(defaultWorkspaceId) {
                        localStorage.setItem('workspace', defaultWorkspaceId)
                    }
                }
                const workspaceId = localStorage.getItem('workspace')

                if(localStorage.getItem('institution') === null) {
                    this.$institutionDataStore.getCurrentWorkspaceInstitution().subscribe(
                        institution => {
                            const institutionId = _.get(institution, '_embedded.institution[0].id')
                            if(institutionId) {
                                localStorage.setItem('institution', institutionId)
                                return fetch.api(`/users/token?institutionId=${_.get(institution, '_embedded.institution[0].id')}&workspaceId=${localStorage.getItem('workspace')}`,
                                {
                                    method: 'GET',
                                })
                            }
                        }
                    )
                }

                const userLocale = userSelectors.defaultInterfaceLanguage(authResult.user);
                // `.messages` exists on `VueI18n` but not on the bare `$i18n` union type.
                fetchAllLocales(Object.keys((this.$i18n as any).messages)).subscribe()

                // @ts-ignore
                const i18nLocale = getI18nLocaleFromAppLocale(userLocale) ?? userLocale
                // @ts-ignore
                this.$root.$i18n.locale = userLocale
                // @ts-ignore
                localStorage.setItem('preferredLocale', userLocale)

                const localStorageDateFormat = localStorage.getItem('dateFormat')
                if(authResult.authMethod === 'SSO' && localStorageDateFormat) {
                    localStorage.setItem('dateFormat', cleanDateFormat(localStorageDateFormat))
                } else {

                    TranslationDataStore.getInstance().getDateFormat().subscribe(dateFormat => {
                        if(dateFormat) {
                            const cleanedDateFormat = cleanDateFormat(dateFormat as string)
                            console.info('Saving date format in local storage: ', cleanedDateFormat)
                            localStorage.setItem('dateFormat', cleanedDateFormat)
                        } else {
                            console.error('Failed to retrieve date format from server. Date format: ', dateFormat)
                        }
                    })
                }

                if(workspaceId) {
                    await this.$workspaceDataStore.fetchOne(workspaceId).pipe(
                        tap(workspace => {
                            const workspaceType = workspaceSelectors.type(workspace)
                            if(workspaceType) {
                                localStorage.setItem('workspaceType', workspaceType)
                            }
                        })
                    ).toPromise()
                }

                this.$router.push({ name: this.homeRoute });
                return;
            }
        },
    },
    created() {
        if (this.$route.query.fromSSO !== undefined) {
            console.log('fromSSO route detected')
            const ssoUserData = sessionStorage.getItem('ssoUserData')
            if (ssoUserData === null) {
               this.loginErrorMessage = this.$t('auth.sso.errors.authenticationFailed') as string
            }
        }
        this.getAuthentificationMethods()
        this.subscribeTo(this.$authService.user$, this.onUser);
    },
})
